export const abbVieProNavigation = [
  'Home',
  'Clinical Profile',
  'Safety Profile',
  'Initiation and Dosing',
  'Resources and Events',
  'Contact Us',
]

export const abbVieProBranches = [
  {
    title: 'Clinical profile',
    description: 'Efficacy, study design and primary endpoints',
    icon: 'clinical',
  },
  {
    title: 'Safety profile',
    description: 'Safety information and supporting evidence',
    icon: 'safety',
  },
  {
    title: 'Initiation and dosing',
    description: 'A clear route through treatment initiation',
    icon: 'dosing',
  },
  {
    title: 'Resources and events',
    description: 'Practical tools for healthcare professionals',
    icon: 'resources',
  },
] as const

export const currentStateAssessment = [
  {
    label: 'AbbVie Pro portal',
    title: 'An existing, trusted channel',
    detail: 'The digital home for HCP engagement — but one channel among many competing for attention.',
    tone: 'neutral',
  },
  {
    label: 'HCP website',
    title: 'Comprehensive, but overwhelming',
    detail: 'Deep, complete content that leaves users facing decision paralysis before they even start.',
    tone: 'neutral',
  },
  {
    label: 'The challenge',
    title: 'High-value actions go unfinished',
    detail: 'Registrations, downloads and requests stall as users get lost in content volume.',
    tone: 'warning',
  },
  {
    label: 'The opportunity',
    title: 'Snackify without rebuilding',
    detail: "Apply Snakker's snackification methodology inside the infrastructure AbbVie already has.",
    tone: 'positive',
  },
] as const

export const episodeChapters = [
  {
    number: '01',
    label: 'Episode 1',
    title: 'Take back control',
    caption: 'A lean-back cinematic open - no clicks required.',
  },
  {
    number: '02',
    label: 'Episode 2',
    title: 'What ulcerative colitis really costs',
    caption: 'Pure science, unbranded - framed around the patient.',
  },
  {
    number: '03',
    label: 'Episode 3',
    title: 'Where SKYRIZI fits',
    caption: 'A 60/40 science-to-brand transition.',
  },
  {
    number: '04',
    label: 'Episode 4',
    title: 'The evidence, distilled',
    caption: 'Primary endpoints made clear, without oversimplifying.',
  },
  {
    number: '05',
    label: 'Episode 5',
    title: 'Continue in AbbVie Pro',
    caption: 'Branch into clinical profile, safety, dosing or resources.',
  },
] as const

export const progressiveEngagementFramework = [
  {
    stage: 'Promise',
    description: 'Episode 1 opens lean-back and low effort - the reward is made obvious immediately.',
  },
  {
    stage: 'Effort',
    description: 'Each following episode asks for slightly more interaction, never more than trust allows.',
  },
  {
    stage: 'Reward',
    description: 'Every step delivers a clear payoff, measured to spot exactly where drop-off happens.',
  },
] as const

export const deploymentPrinciples = [
  {
    title: 'No portal rebuild',
    detail: 'Snaks embed inside AbbVie Pro without changing its existing flow or navigation.',
    icon: 'embed',
  },
  {
    title: 'Gated where required',
    detail: 'Content sits behind login in markets like Finland and Denmark, as regulations require.',
    icon: 'gate',
  },
  {
    title: 'Parallel pathways',
    detail: 'Users choose a lean-back video or traditional text browsing - never forced into one.',
    icon: 'split',
  },
] as const

export const chapterProgressionModel = [
  {
    chapter: 'Chapter 1',
    sciencePercent: 100,
    brandPercent: 0,
    description: 'Pure science, unbranded messaging that earns credibility first.',
  },
  {
    chapter: 'Chapter 2',
    sciencePercent: 60,
    brandPercent: 40,
    description: '60/40 science-to-brand transition introduces the product in context.',
  },
  {
    chapter: 'Chapter 3',
    sciencePercent: 40,
    brandPercent: 60,
    description: '40/60 brand-to-science positioning, once relevance is established.',
  },
] as const

export const highValueActions = [
  {
    action: 'HCP registration',
    detail: 'Convert anonymous visits into known, addressable contacts.',
    icon: 'register',
  },
  {
    action: 'Resource downloads',
    detail: 'Move users from awareness into deeper clinical material.',
    icon: 'download',
  },
  {
    action: 'Booth visits & meeting requests',
    detail: 'Bridge digital engagement into real field conversations.',
    icon: 'meeting',
  },
] as const

export const storytellingBeats = [
  {
    beat: 'Opening hook',
    description: 'A provocative insight or question triggers curiosity in seconds.',
  },
  {
    beat: 'Middle development',
    description: 'The story builds context and relevance at a comfortable pace.',
  },
  {
    beat: 'Climax',
    description: 'The key clinical or scientific payoff lands with full weight.',
  },
  {
    beat: 'Resolution',
    description: 'A clear next step channels momentum into action.',
  },
] as const

export const narrativeElements = [
  {
    title: 'Provocative hook',
    description: 'Lead with an unexpected insight or question, not a disclaimer.',
  },
  {
    title: 'Effort calibration',
    description: 'Minimise friction early; increase interactivity only as trust builds.',
  },
  {
    title: 'Reward types',
    description: 'Knowledge, peer insight, and the small dopamine hit of discovery.',
  },
] as const

export const regionalMarkets = [
  {
    market: 'Finland',
    requirement: 'Login required',
    note: 'Content is gated behind HCP authentication.',
  },
  {
    market: 'Denmark',
    requirement: 'Login required',
    note: 'Content is gated behind HCP authentication.',
  },
  {
    market: 'Sweden',
    requirement: 'No login required',
    note: 'The website is the primary asset - justifies higher content investment.',
  },
  {
    market: 'Nordic region',
    requirement: 'Cross-affiliate coordination',
    note: 'Aligned across Scandinavian, Finnish, Dutch and Irish affiliates.',
  },
] as const

export const collaborationRoles = [
  {
    owner: 'Snakker',
    responsibility: 'Creative execution and technical delivery of every episode.',
  },
  {
    owner: 'AbbVie',
    responsibility: 'Content, strategic direction and approval gates.',
  },
  {
    owner: 'Together',
    responsibility: 'Iterative feedback loops on engagement data.',
  },
] as const

export const expectedOutcomes = [
  {
    title: 'Increased HCP engagement',
    detail: 'Lean-back entry points reduce decision paralysis.',
  },
  {
    title: 'Higher conversion rates',
    detail: 'Progressive engagement guides users to high-value actions.',
  },
  {
    title: 'Scalable content',
    detail: 'Snack assets repurpose across congress, email and social.',
  },
  {
    title: 'Measurable ROI',
    detail: 'Continuous optimisation based on drop-off analysis.',
  },
] as const
